import { useEffect, useState } from 'react';
import type { DialectId, DialectTypeCatalog } from '@bits2schema/shared';
import { api } from '../api/client';

/**
 * Katalog typů (E10-US3/US4) je per-dialekt statický, proto se cachuje
 * v modulové proměnné napříč celou session — žádný důvod pro refetch,
 * ani pro těžší cache knihovnu.
 */
const cache = new Map<DialectId, DialectTypeCatalog>();
const pending = new Map<DialectId, Promise<DialectTypeCatalog>>();

async function loadTypeCatalog(dialectId: DialectId): Promise<DialectTypeCatalog> {
  const cached = cache.get(dialectId);
  if (cached) return cached;
  let inFlight = pending.get(dialectId);
  if (!inFlight) {
    inFlight = api.getTypeCatalog(dialectId).then(({ catalog }) => {
      cache.set(dialectId, catalog);
      pending.delete(dialectId);
      return catalog;
    });
    pending.set(dialectId, inFlight);
  }
  return inFlight;
}

/** `null` dokud katalog nedorazí (poprvé v projektu) — volající zobrazí `common.loading` apod. */
export function useTypeCatalog(dialectId: DialectId): DialectTypeCatalog | null {
  const [catalog, setCatalog] = useState<DialectTypeCatalog | null>(cache.get(dialectId) ?? null);

  useEffect(() => {
    let cancelled = false;
    const existing = cache.get(dialectId);
    if (existing) {
      setCatalog(existing);
      return;
    }
    void loadTypeCatalog(dialectId).then((loaded) => {
      if (!cancelled) setCatalog(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [dialectId]);

  return catalog;
}
